import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import ModuleLayout from '../components/ModuleLayout';
import FlashReport from '../components/accounting/FlashReport';
import ExportToolbar from '../components/accounting/ExportToolbar';

export default function FlashReportPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;
  const [reportData, setReportData] = useState(null);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleDataReady = useCallback((d) => { setReportData(d); }, []);

  const buildReport = useCallback(() => {
    if (!reportData) return null;
    const d = reportData;
    const sar = t('common.sar');
    return {
      kpis: [
        { label: t('accounting.totalRevenue'), value: fmt(d.revenue), unit: sar, highlight: 'positive' },
        { label: t('accounting.totalExpenses'), value: fmt(d.expenses), unit: sar },
        { label: t('accounting.netProfit'), value: fmt(d.net_profit), unit: sar, highlight: d.net_profit >= 0 ? 'positive' : 'negative' },
        { label: t('flash.avgOrder'), value: fmt(d.avg_order), unit: sar },
        { label: t('flash.profitMargin'), value: `${d.profit_margin}%`, highlight: d.profit_margin >= 0 ? 'positive' : 'negative' },
        { label: t('accounting.vatCollected'), value: fmt(d.vat_collected), unit: sar },
      ],
      sections: [
        {
          type: 'comparison', title: '',
          items: [
            { label: t('flash.vsYesterday'), reference: `${fmt(d.yesterday_revenue)} ${sar}`, change: d.vs_yesterday },
            { label: t('flash.vsLastWeek'), reference: `${fmt(d.last_week_revenue)} ${sar}`, change: d.vs_last_week },
          ]
        },
        {
          type: 'table', title: t('flash.topItems'),
          headers: ['#', t('common.name'), t('waste.qty'), t('accounting.totalRevenue')],
          rows: (d.top_items || []).map((item, i) => ({
            cells: [
              { value: i + 1 },
              { value: item.name },
              { value: item.qty, type: 'num' },
              { value: `${fmt(item.revenue)} ${sar}`, type: 'num' },
            ]
          }))
        },
        {
          type: 'table', title: t('accounting.revenueByPayment'),
          headers: [t('accounting.paymentMethod'), t('accounting.amount'), t('flash.orders')],
          rows: Object.entries(d.revenue_by_payment || {}).map(([method, v]) => ({
            cells: [
              { value: method === 'cash' ? t('accounting.cash') : method === 'card' ? t('accounting.card') : t('accounting.transfer') },
              { value: `${fmt(v.total)} ${sar}`, type: 'num' },
              { value: v.count, type: 'num' },
            ]
          }))
        },
        {
          type: 'kpis', title: t('flash.cashStatus'),
          items: [
            { label: t('accounting.difference'), value: fmt(d.cash_status?.difference), unit: sar, highlight: d.cash_status?.difference === 0 ? 'positive' : 'negative' },
            { label: t('flash.waste'), value: fmt(d.waste?.total_cost), unit: sar, highlight: d.waste?.total_cost > 0 ? 'negative' : '' },
          ]
        },
      ]
    };
  }, [reportData, t, lang, fmt]);

  const csvData = reportData?.top_items?.length ? {
    data: reportData.top_items,
    columns: [
      { key: 'name', label: t('common.name') },
      { key: 'qty', label: t('waste.qty') },
      { key: 'revenue', label: t('accounting.totalRevenue') },
    ]
  } : null;

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-5" data-testid="flash-report-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('accounting.flashReport')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeBranch?.name || ''}</p>
          </div>
          <ExportToolbar
            title={`${t('accounting.flashReport')} - ${reportData?.date || ''}`}
            subtitle={activeBranch?.name}
            buildReport={buildReport}
            csvData={csvData}
            t={t} lang={lang}
          />
        </div>
        <FlashReport branchId={branchId} token={token} t={t} lang={lang} onDataReady={handleDataReady} />
      </div>
    </ModuleLayout>
  );
}
